import { api } from "../../api/client";

export type TaskStatus = "open" | "in_progress" | "done" | "cancelled";
export type TaskPriority = "low" | "medium" | "high" | "urgent";
export type RelatedType = "case" | "intel" | "poi" | "performance" | "popularity";

export type TaskSource = "manual" | "auto";

export interface Task {
  id: string;
  // Short identifier (e.g. TSK-A2B3) — generated server-side. Recipients see
  // ONLY this; title/description/assignedBy are stripped from their view and
  // arrive undefined.
  code?: string;
  title?: string;
  description?: string;
  assigneeUid: string;
  assignedBy?: string;
  assignedAt: string;
  branchId: string;
  wardId?: string | null;
  constituencyId?: string | null;
  pollingStationId?: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  dueAt: string | null;
  relatedType?: RelatedType | null;
  relatedId?: string | null;
  completedAt: string | null;
  completedBy?: string | null;
  // `auto` means the daily auto-task engine generated this task. `manual`
  // (default) means a person filed it via the wizard. Used to render an
  // "Auto" badge in the tasks list.
  source?: TaskSource;
  autoTaskKey?: string | null;
}

interface List<T> { items: T[]; }

export interface TaskCreate {
  title: string;
  description?: string;
  assigneeUid: string;
  branchId: string;
  pollingStationId?: string;
  priority?: TaskPriority;
  dueAt?: string;
  relatedType?: RelatedType;
  relatedId?: string;
}

export const TasksApi = {
  list: (filters?: { status?: TaskStatus; assigneeUid?: string }) => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set("status", filters.status);
    if (filters?.assigneeUid) qs.set("assigneeUid", filters.assigneeUid);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return api<List<Task>>(`/tasks${suffix}`);
  },
  myTasks: (status?: TaskStatus) => {
    const suffix = status ? `?status=${status}` : "";
    return api<List<Task>>(`/me/tasks${suffix}`);
  },
  get: (id: string) => api<Task>(`/tasks/${id}`),
  create: (body: TaskCreate) =>
    api<Task>("/tasks", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Omit<Task, "id">>) =>
    api<Task>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) =>
    api<void>(`/tasks/${id}`, { method: "DELETE" }),

  registerFcm: (token: string, platform: "android" | "web" | "ios" = "web") =>
    api<{ ok: boolean }>("/me/fcm-tokens", {
      method: "POST",
      body: JSON.stringify({ token, platform }),
    }),
  unregisterFcm: (token: string) =>
    api<void>(`/me/fcm-tokens/${encodeURIComponent(token)}`, { method: "DELETE" }),
};
